<template>
  <main
    class="page"
    role="main"
    aria-labelledby="page-title"
  >
    <header class="page-header">
      <h1 id="page-title">
        Auswertung
      </h1>
    </header>

    <!-- Kachelraster -->
    <section
      ref="tilesEl"
      class="tiles"
      :class="{'tiles--stacked': isStacked}"
    >
      <button
        v-if="auth.isAdmin || auth.isTeacher"
        class="tile"
        :class="{active: activeTile === 'absence-days'}"
        @click="selectTile('absence-days')"
      >
        <span class="tile-title">Fehlzeiten</span>
        <span class="tile-sub">Fehltage und Fehlstunden</span>
      </button>

      <button
        v-if="auth.isAdmin || auth.isTeacher"
        class="tile"
        :class="{active: activeTile === 'mandatory-last-lessons'}"
        @click="selectTile('mandatory-last-lessons')"
      >
        <span class="tile-title">letzten 5 Angebotstermine</span>
        <span class="tile-sub">Anwesenheit bei Pflichtangeboten</span>
      </button>

      <button
        v-if="auth.isAdmin"
        class="tile"
        :class="{active: activeTile === 'stays-holiday-ops'}"
        @click="selectTile('stays-holiday-ops')"
      >
        <span class="tile-title">Fehlbuchungen</span>
        <span class="tile-sub">leerer Aufenthalt & Ferien</span>
      </button>

      <button
        v-if="auth.isAdmin"
        class="tile"
        :class="{active: activeTile === 'export-forms'}"
        @click="selectTile('export-forms')"
      >
        <span class="tile-title">Meldezettel exportieren</span>
        <span class="tile-sub">Akzeptierte Meldungen als PDF</span>
      </button>

      <button
        v-if="auth.isAdmin"
        class="tile"
        :class="{active: activeTile === 'password-resets'}"
        @click="selectTile('password-resets')"
      >
        <span class="tile-title">Passwort-Resets</span>
        <span class="tile-sub">Anfragen & gesetzte Passwörter</span>
      </button>

      <button
        v-if="auth.isAdmin"
        class="tile"
        :class="{active: activeTile === 'login-devices'}"
        @click="selectTile('login-devices')"
      >
        <span class="tile-title">Geräteinfo</span>
        <span class="tile-sub">Geräte & Auflösungen je Login</span>
      </button>

      <button
        v-if="auth.isAdmin || auth.isTeacher"
        class="tile"
        :class="{active: activeTile === 'competency-grids'}"
        @click="selectTile('competency-grids')"
      >
        <span class="tile-title">Lernbriefe</span>
        <span class="tile-sub">Vorlagen & Rückmeldungsbögen</span>
      </button>
    </section>

    <!-- Ausgewählte Card -->
    <section
      v-if="auth.isAdmin || auth.isTeacher"
      class="mt-12"
    >
      <AbsenceDaysCard v-if="activeTile === 'absence-days'" />
      <MandatoryLastLessonsCard v-else-if="activeTile === 'mandatory-last-lessons'" />
      <StaysHolidayOpsCard v-else-if="activeTile === 'stays-holiday-ops'" />
      <ExportFormsCard v-else-if="activeTile === 'export-forms'" />
      <PasswordResetsCard v-else-if="activeTile === 'password-resets'" />
      <LoginDevicesCard v-else-if="activeTile === 'login-devices'" />
      <CompetencyGridsCard v-else-if="activeTile === 'competency-grids'" />
    </section>
  </main>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, nextTick} from 'vue';
import {useAuthStore} from '@/stores/auth';
import AbsenceDaysCard from './analytics/AbsenceDaysCard.vue';
import MandatoryLastLessonsCard from './analytics/MandatoryLastLessonsCard.vue';
import StaysHolidayOpsCard from './analytics/StaysHolidayOpsCard.vue';
import ExportFormsCard from './analytics/ExportFormsCard.vue';
import PasswordResetsCard from './analytics/PasswordResetsCard.vue';
import LoginDevicesCard from './analytics/LoginDevicesCard.vue';
import CompetencyGridsCard from './analytics/CompetencyGridsCard.vue';

const auth = useAuthStore();

// Standard: Fehlzeiten-Card vorauswählen
const activeTile = ref('absence-days');

function selectTile(key) {
  activeTile.value = key;
}

// "Karteikarten"-Fächerung bei Zeilenumbruch
const tilesEl = ref(null);
const isStacked = ref(false);

function updateTileStacking() {
  const el = tilesEl.value;
  if (!el) return;

  const tiles = Array.from(el.querySelectorAll('.tile'));
  if (tiles.length === 0) {
    isStacked.value = false;
    el.style.setProperty('--stack-rows', '1');
    return;
  }

  // nach Zeilen (offsetTop) gruppieren
  const rows = new Map();
  for (const tile of tiles) {
    const top = tile.offsetTop;
    if (!rows.has(top)) rows.set(top, []);
    rows.get(top).push(tile);
  }

  // "stacked" nur dann, wenn wirklich umgebrochen wurde (mehr als 1 Zeile)
  isStacked.value = rows.size > 1;

  // Anzahl Zeilen als CSS-Variable setzen
  el.style.setProperty('--stack-rows', String(Math.max(1, rows.size)));

  // Reihenfolge der Zeilen von oben nach unten
  const rowTops = Array.from(rows.keys()).sort((a, b) => a - b);

  // pro Tile: Zeilenindex + Index innerhalb der Zeile setzen
  for (const [rowIndex, top] of rowTops.entries()) {
    const rowTiles = rows.get(top) ?? [];
    rowTiles.forEach((tile, idx) => {
      tile.style.setProperty('--stack-row', String(rowIndex));
      tile.style.setProperty('--stack-i', String(idx));
    });
  }
}

// Resize-Handling ohne ResizeObserver (verhindert "ResizeObserver loop ...")
let rafId = 0;
function scheduleUpdateTileStacking() {
  if (rafId) return;
  rafId = window.requestAnimationFrame(() => {
    rafId = 0;
    updateTileStacking();
  });
}

onMounted(async () => {
  await nextTick();
  scheduleUpdateTileStacking();

  window.addEventListener('resize', scheduleUpdateTileStacking, {passive: true});
  window.addEventListener('orientationchange', scheduleUpdateTileStacking, {passive: true});
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', scheduleUpdateTileStacking);
  window.removeEventListener('orientationchange', scheduleUpdateTileStacking);

  if (rafId) {
    window.cancelAnimationFrame(rafId);
    rafId = 0;
  }
});
</script>

<style scoped>
.page {
  padding: 16px;
  background: var(--bg);
  color: var(--text);
}

:deep(.overlay) {
  z-index: 1000;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border);
}

.h1 {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  margin: 0;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;

  /* Overlap-Parameter für "Karteikarten"-Look */
  --stack-overlap-y: 38px;

  /* Default (kein Umbruch) */
  --stack-rows: 1;
}

.tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;

  padding: 14px;
  height: 70px;

  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  cursor: pointer;
  text-align: left;

  transition: transform 160ms cubic-bezier(0.2, 0, 0, 1), background 160ms cubic-bezier(0.2, 0, 0, 1),
  border-color 160ms cubic-bezier(0.2, 0, 0, 1);
  will-change: transform;
}

/* Standardzustand bei Umbruch: vertikal falten UND Platz schaffen */
.tiles.tiles--stacked:not(:hover):not(:focus-within) {
  row-gap: 0;

  /* zieht den nachfolgenden Content nach oben (visuelle Höhe reduziert) */
  margin-bottom: calc(-1 * var(--stack-overlap-y) * (var(--stack-rows) - 1));
}

.tiles.tiles--stacked:not(:hover):not(:focus-within) .tile {
  transform: translateY(calc(-1 * var(--stack-overlap-y) * var(--stack-row, 0)));
  z-index: calc(100 + var(--stack-row, 0));

  /* explizit: keine Transparenz */
  opacity: 1;
}

/* Bei Hover/Fokus: wieder normaler Abstand, kein Hochziehen */
.tiles.tiles--stacked:hover {
  row-gap: 12px;
  margin-bottom: 0;
}

.tiles.tiles--stacked:hover .tile,
.tiles.tiles--stacked:focus-within .tile {
  transform: translateY(0);
}

.tile:hover {
  background: color-mix(in srgb, var(--primary) 8%, white);
}

.tile.active {
  border-color: rgba(79, 70, 229, .4);
  background: color-mix(in srgb, var(--primary) 12%, white);
}

.tile-title {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.tile-sub {
  font-size: 12px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.mt-12 {
  margin-top: 12px;
}
</style>